import { obbToPlanes } from "@/hooks/use-clipping-planes";
import { useCurrentModelBoundingBox } from "@/modes/overview-mode/use-update-overview-camera";
import { CadModelObject, PointCloudObject } from "@/object-cache";
import { Features, selectHasFeature } from "@/store/features/features-slice";
import { useAppSelector, useAppStore } from "@/store/store-hooks";
import { selectObjectVisibility } from "@/store/view-options/view-options-selectors";
import { ViewObjectTypes } from "@/store/view-options/view-options-slice";
import {
  getSheetCenter,
  selectIElementWorldTransform,
} from "@faro-lotv/app-component-toolbox";
import { IElementGenericImgSheet } from "@faro-lotv/ielement-types";
import { selectProjectId } from "@faro-lotv/project-source";
import { useFrame, useThree } from "@react-three/fiber";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
} from "react";
import {
  Box3,
  Camera,
  CanvasTexture,
  MathUtils,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  OrthographicCamera,
  PerspectiveCamera,
  Plane,
  PlaneGeometry,
  Quaternion,
  SRGBColorSpace,
  Vector3,
} from "three";
import { useFallbackTiles } from "./use-fallback-tiles";
import { toMapLocal, WorldMapPose } from "./world-map-alignment";
import { renderBasemapTile } from "./world-map-basemap";
import { WorldMapSceneView } from "./world-map-scene-view";
import { useWorldMapStore } from "./world-map-store";
import {
  computeVisibleTiles,
  GeoPosition,
  WorldMapStyle,
  WorldMapTile,
} from "./world-map-tiles";

/** Distance of the map below the lowest point of the model, to avoid z-fighting with the floor */
const FLOOR_OFFSET_M = 0.05;
/** Assumed size of the data when only a sheet is available */
const DEFAULT_DATA_RADIUS_M = 20;
const ON_TOP_OPACITY = 0.7;
const ON_TOP_RENDER_ORDER = 1000;
const TILES_UPDATE_INTERVAL_S = 0.25;
/** Keeps the finest level reasonable when the camera is right above the map */
const MIN_CAMERA_HEIGHT_M = 1;
const MAX_UNUSED_TILE_MESHES = 150;
/** Larger than the zoom range, so fallback tiles render before all current tiles */
const FALLBACK_RENDER_ORDER_SHIFT = 100;

type WorldMapPlacementProps = {
  /** The sheet of the active area, the map is centered on it when available */
  sheet?: IElementGenericImgSheet;
  /** The point cloud the map is placed below */
  pointCloud?: PointCloudObject | null;
  /** The CAD model the map is placed below */
  cadModel?: CadModelObject | null;
};

type WorldMapRendererProps = WorldMapPlacementProps & {
  /** True for the flat map of the 2D view, otherwise the 3D map of the Dashboard is shown */
  isFlat?: boolean;
  /** The clipping planes of the 3D scene, the 3D buildings inside the clipping box are hidden */
  clippingPlanes?: Plane[];
};

/** @returns The Dashboard coordinates and the local alignment of the current project */
export function useCurrentProjectWorldMap(): {
  position: GeoPosition | null;
  pose?: WorldMapPose;
} {
  const projectId = useAppSelector(selectProjectId);
  const projectPosition = useWorldMapStore((state) => state.projectPosition);
  return projectPosition && projectPosition.projectId === projectId
    ? projectPosition
    : { position: null };
}

/**
 * @param props The sheet and models the map is placed below
 * @returns Where the map is placed: the user's alignment if any, otherwise centered on the sheet (or the model)
 */
export function useWorldMapPlacement({
  sheet,
  pointCloud = null,
  cadModel = null,
}: WorldMapPlacementProps): {
  pose: WorldMapPose;
  height: number;
  /** Center of the sheet (or the model) in the scene */
  center: Vector3;
  /** Radius of the model around its center, in meters */
  radius: number;
  /** Bounds of the model in the scene, if any */
  box?: Box3;
} {
  const { pose } = useCurrentProjectWorldMap();
  const box = useCurrentModelBoundingBox(pointCloud, cadModel);
  const store = useAppStore();

  // The sheet is the shared reference of the 2D and 3D views, so the map lands on the same spot in both
  const placement = useMemo(() => {
    const transform = sheet
      ? selectIElementWorldTransform(sheet.id)(store.getState())
      : undefined;
    const sheetCenter =
      sheet && transform
        ? getSheetCenter(sheet, new Matrix4().fromArray(transform.worldMatrix))
        : undefined;
    const center =
      sheetCenter ?? box?.getCenter(new Vector3()) ?? new Vector3();
    const floor = Math.min(
      sheetCenter?.y ?? Infinity,
      box?.min.y ?? Infinity,
      center.y,
    );
    return {
      defaultPose: { x: center.x, z: center.z, rotation: 0 },
      height: floor - FLOOR_OFFSET_M,
      center,
      radius: box
        ? box.getSize(new Vector3()).length() / 2
        : DEFAULT_DATA_RADIUS_M,
    };
  }, [box, sheet, store]);

  return {
    pose: pose ?? placement.defaultPose,
    height: placement.height,
    center: placement.center,
    radius: placement.radius,
    box,
  };
}

/**
 * @param props The sheet and models the map is placed below
 * @param props.isFlat True to draw the flat map of the 2D view
 * @returns The world map layer below the current sheet and model, when enabled by the user
 */
export function WorldMapRenderer({
  isFlat,
  clippingPlanes,
  ...props
}: WorldMapRendererProps): JSX.Element | null {
  const hasFeature = useAppSelector(selectHasFeature(Features.WorldMapLayer));
  const isVisible = useAppSelector(
    selectObjectVisibility(ViewObjectTypes.worldMap),
  );
  const { position } = useCurrentProjectWorldMap();

  if (!hasFeature || !isVisible || !position) return null;
  return isFlat ? (
    <WorldMap position={position} {...props} />
  ) : (
    <WorldMap3D
      position={position}
      clippingPlanes={clippingPlanes}
      {...props}
    />
  );
}

/** @returns The Dashboard's ArcGIS 3D map behind the scene, placed like the flat map */
function WorldMap3D({
  position,
  clippingPlanes,
  ...props
}: WorldMapPlacementProps & {
  position: GeoPosition;
  clippingPlanes?: Plane[];
}): JSX.Element {
  const { box, ...placement } = useWorldMapPlacement(props);
  // Without a clipping box, the buildings are cut away around the data, like the default clipping box
  const planes = useMemo(
    () =>
      clippingPlanes?.length || !box || box.isEmpty()
        ? clippingPlanes
        : obbToPlanes({
            position: box.getCenter(new Vector3()),
            quaternion: new Quaternion(),
            size: box.getSize(new Vector3()),
          }),
    [clippingPlanes, box],
  );
  return (
    <WorldMapSceneView
      position={position}
      clippingPlanes={planes}
      {...placement}
    />
  );
}

/** @returns The flat map of the 2D view, drawn with three.js below the sheet */
function WorldMap({
  position,
  ...props
}: WorldMapPlacementProps & { position: GeoPosition }): JSX.Element {
  const style = useWorldMapStore((state) => state.style);
  // While the user picks a point on the map, show the map on top of the data
  const isPickingOnMap = useWorldMapStore(
    ({ alignmentPicks }) => !!alignmentPicks && alignmentPicks.length % 2 === 1,
  );
  const { pose, height } = useWorldMapPlacement(props);
  const selectTiles = useCallback(
    (focus: MapFocus) =>
      computeVisibleTiles(position, focus, focus.metersPerPixel, style),
    [position, style],
  );
  const tiles = useViewDependentSelection(pose, height, selectTiles);
  const { fallbackTiles, onTileSettled } = useFallbackTiles(tiles);

  return (
    <group
      position={[pose.x, height, pose.z]}
      rotation-y={pose.rotation}
      name="WorldMap"
    >
      {[
        ...fallbackTiles.map((tile) => ({ tile, isFallback: true })),
        ...tiles.map((tile) => ({ tile, isFallback: false })),
      ].map(({ tile, isFallback }) => (
        <WorldMapTileMesh
          key={tile.key}
          tile={tile}
          style={style}
          isOnTop={isPickingOnMap}
          isFallback={isFallback}
          onSettled={onTileSettled}
        />
      ))}
    </group>
  );
}

/** Where the user looks at the map, in map coordinates */
type MapFocus = {
  east: number;
  north: number;
  /** Size of a screen pixel at the focus point, in meters */
  metersPerPixel: number;
};

/**
 * Recomputes what to show of the map while the camera moves, at a limited rate.
 *
 * @param pose The placement of the map in the scene
 * @param height The height of the map in the scene
 * @param select Memoized function returning the items to show around a focus point
 * @returns The selected items, updated only when their keys change
 */
function useViewDependentSelection<T extends { key: string }>(
  pose: WorldMapPose,
  height: number,
  select: (focus: MapFocus) => T[],
): T[] {
  const [items, setItems] = useState<T[]>([]);
  const itemsKey = useRef("");
  const sinceUpdate = useRef(Infinity);
  const invalidate = useThree((state) => state.invalidate);

  // The scene may render on demand only, so request a frame to select the first items
  useEffect(() => {
    sinceUpdate.current = Infinity;
    invalidate();
  }, [pose, height, select, invalidate]);

  useFrame(({ camera, size }, delta) => {
    sinceUpdate.current += delta;
    if (sinceUpdate.current < TILES_UPDATE_INTERVAL_S) return;
    sinceUpdate.current = 0;

    const { focus, metersPerPixel } = computeViewFocus(
      camera,
      size.height,
      height,
    );
    const [east, z] = toMapLocal(pose, [focus.x, focus.z]);
    const next = select({ east, north: -z, metersPerPixel });
    const nextKey = next.map((item) => item.key).join();
    if (nextKey !== itemsKey.current) {
      itemsKey.current = nextKey;
      setItems(next);
    }
  });

  return items;
}

/**
 * @param camera The camera looking at the map
 * @param viewportHeight The height of the view in pixels
 * @param groundHeight The height of the map in the scene
 * @returns The point the detail of the map is centered on, and the size of a screen pixel there
 */
function computeViewFocus(
  camera: Camera,
  viewportHeight: number,
  groundHeight: number,
): { focus: Vector3; metersPerPixel: number } {
  if (camera instanceof OrthographicCamera) {
    // Center of the view on the map
    const direction = camera.getWorldDirection(new Vector3());
    const distance =
      Math.abs(direction.y) > 1e-3
        ? (groundHeight - camera.position.y) / direction.y
        : 0;
    return {
      focus: camera.position.clone().addScaledVector(direction, distance),
      metersPerPixel:
        (camera.top - camera.bottom) / camera.zoom / viewportHeight,
    };
  }
  // Below the camera, where the map is closest and needs the most detail
  const fov = camera instanceof PerspectiveCamera ? camera.fov : 60;
  const heightAboveMap = Math.max(
    Math.abs(camera.position.y - groundHeight),
    MIN_CAMERA_HEIGHT_M,
  );
  return {
    focus: camera.position.clone(),
    metersPerPixel:
      (2 * heightAboveMap * Math.tan((fov * MathUtils.DEG2RAD) / 2)) /
      viewportHeight,
  };
}

/** Tile meshes that left the view recently, most recent last, to show them again instantly */
const unusedTileMeshes = new Map<
  string,
  Mesh<PlaneGeometry, MeshBasicMaterial>
>();

function releaseTileMesh(
  key: string,
  mesh: Mesh<PlaneGeometry, MeshBasicMaterial>,
): void {
  unusedTileMeshes.set(key, mesh);
  for (const [oldKey, old] of unusedTileMeshes) {
    if (unusedTileMeshes.size <= MAX_UNUSED_TILE_MESHES) break;
    unusedTileMeshes.delete(oldKey);
    old.geometry.dispose();
    old.material.map?.dispose();
    old.material.dispose();
  }
}

/** @returns A flat map tile, with east along +X and north along -Z */
function WorldMapTileMesh({
  tile,
  style,
  isOnTop,
  isFallback,
  onSettled,
}: {
  tile: WorldMapTile;
  style: WorldMapStyle;
  /** True to draw the tile semi-transparent over everything else */
  isOnTop: boolean;
  /** True to draw the tile behind the current tiles, while they load */
  isFallback: boolean;
  /** Called when the tile finished loading (or failed), and with false when it is removed */
  onSettled(key: string, isSettled: boolean): void;
}): JSX.Element | null {
  const invalidate = useThree((state) => state.invalidate);
  const [mesh, setMesh] = useState(() => unusedTileMeshes.get(tile.key));
  const { key, size, east, north, z, x, y } = tile;

  useEffect(() => {
    const cached = unusedTileMeshes.get(key);
    if (cached) {
      unusedTileMeshes.delete(key);
      setMesh(cached);
      onSettled(key, true);
      invalidate();
      return () => {
        onSettled(key, false);
        releaseTileMesh(key, cached);
      };
    }

    let created: Mesh<PlaneGeometry, MeshBasicMaterial> | undefined;
    let cancelled = false;
    renderBasemapTile(style, { z, x, y })
      .then((canvas) => {
        if (cancelled) return;
        const texture = new CanvasTexture(canvas);
        texture.colorSpace = SRGBColorSpace;
        created = new Mesh(
          new PlaneGeometry(size, size).rotateX(-Math.PI / 2),
          new MeshBasicMaterial({ map: texture, toneMapped: false }),
        );
        created.position.set(east, 0, -north);
        // The map is a backdrop, it must not be picked by tools or controls
        created.raycast = () => undefined;
        setMesh(created);
        invalidate();
      })
      // A missing tile (e.g. no imagery at this zoom) must not keep the fallback tiles forever
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) onSettled(key, true);
      });
    return () => {
      cancelled = true;
      onSettled(key, false);
      if (created) releaseTileMesh(key, created);
    };
  }, [key, style, size, east, north, z, x, y, invalidate, onSettled]);

  useLayoutEffect(() => {
    if (!mesh) return;
    Object.assign(mesh.material, {
      transparent: isOnTop,
      opacity: isOnTop ? ON_TOP_OPACITY : 1,
      depthTest: !isOnTop,
      // Fallback tiles are drawn first without depth, so they only fill the gaps of the new tiles
      depthWrite: !isFallback,
      needsUpdate: true,
    });
    mesh.renderOrder =
      (isOnTop ? ON_TOP_RENDER_ORDER + z : 0) -
      (isFallback ? FALLBACK_RENDER_ORDER_SHIFT - z : 0);
    invalidate();
  }, [mesh, isOnTop, isFallback, invalidate, z]);

  return mesh ? <primitive object={mesh} /> : null;
}
